// A table seen from above, drawn with its chairs: the Tables tab's floor and the table
// set-up in Settings both use it. Spans only, so it can sit inside a button.
import { cx } from '@ims/ui'
import type { ReactNode } from 'react'
import type { TableState } from '../lib/orders.ts'

/** A table's state on the floor, or `plain` where it is only being set up. */
export type TableTone = TableState | 'plain'

// Spelled out so Tailwind sees every class.
const TONE: Record<TableTone, { table: string; chair: string; ink: string; swatch?: string }> = {
  // Free is green, as it always was on this screen; its chairs are empty outlines.
  free: {
    table: 'border-dashed border-success/50 bg-success-light/60',
    chair: 'bg-success',
    ink: 'text-success',
  },
  plain: { table: 'border-border bg-surface', chair: 'bg-neutral/50', ink: 'text-muted' },
  busy: {
    table: 'border-kitchen/40 bg-kitchen-light',
    chair: 'bg-kitchen',
    ink: 'text-kitchen',
  },
  // Food ready keeps the seated table (green is "free") and says so with its badge and words.
  ready: {
    table: 'border-success/60 bg-kitchen-light',
    chair: 'bg-kitchen',
    ink: 'text-success',
  },
  pay: { table: 'border-primary/40 bg-primary-light', chair: 'bg-primary', ink: 'text-primary' },
  call: { table: 'border-people/40 bg-people-light', chair: 'bg-people', ink: 'text-people' },
}
/** The class that colours a state's words on its table. */
export const toneInk = (tone: TableTone) => TONE[tone].ink
/** The class of a small square in a state's colour (the legend). */
export const toneSwatch = (tone: TableTone) => TONE[tone].table

const WIDTH = { 1: 'w-32', 2: 'w-32', 3: 'w-44', 4: 'w-56' } as const

/** Chairs along one long side: half the seats, 1 to 4 (seats not set draws a table of 4). */
export function chairsPerSide(seats: number | null): 1 | 2 | 3 | 4 {
  return Math.min(4, Math.max(1, Math.ceil((seats ?? 4) / 2))) as 1 | 2 | 3 | 4
}

function Chairs({ count, filled, fill }: { count: number; filled: number; fill: string }) {
  return (
    <span aria-hidden className="flex justify-center gap-3">
      {Array.from({ length: count }, (_, i) => (
        <span
          key={i}
          className={cx(
            'h-2 w-6 rounded-full',
            i < filled ? fill : 'border-[1.5px] border-border bg-transparent',
          )}
        />
      ))}
    </span>
  )
}

export function TableShape({
  seats,
  guests,
  tone,
  selected = false,
  badge,
  className,
  children,
}: {
  seats: number | null
  /** How many chairs are taken; left out, every chair is drawn filled. */
  guests?: number
  tone: TableTone
  selected?: boolean
  /** A small round mark on the table's corner (food ready, a guest calling). */
  badge?: ReactNode
  className?: string
  children: ReactNode
}) {
  const per = chairsPerSide(seats)
  const taken = guests ?? per * 2
  const look = TONE[tone]
  return (
    <span className={cx('flex flex-col gap-1.5', WIDTH[per], className)}>
      <Chairs count={per} filled={Math.min(per, Math.ceil(taken / 2))} fill={look.chair} />
      <span
        className={cx(
          'relative flex min-h-[4.75rem] flex-col items-center justify-center rounded-card border-[1.5px] px-1.5 py-2 text-center',
          look.table,
          selected && 'ring-2 ring-primary ring-offset-2 ring-offset-surface',
        )}
      >
        {badge}
        {children}
      </span>
      <Chairs count={per} filled={Math.min(per, Math.floor(taken / 2))} fill={look.chair} />
    </span>
  )
}
